import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, map, of, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Doctor, DoctorListItem, DoctorPatientResponse, DoctorResponse, DoctorsListResponse, FetchDoctorsQueryParam, MedicSimpleListResponse } from '../../models/doctor';
import { StatusMessageResponse } from '../../models/utils/status-message-response';

@Injectable({
  providedIn: 'root',
})
export class DoctorService {
  private _apiUrl = environment.apiUrl;
  private _http = inject(HttpClient);
  private jsonUrl = 'assets/mock-data/doctor.json';
  private _authHeaders = new HttpHeaders({
    Authorization: '',
  });
  private countMedic: number = 1000;
  constructor() {}

  #doctorsList = new BehaviorSubject<Doctor[]>([]);

  private doctors: DoctorPatientResponse[] = [];

  public fetchDoctorsOrder(): Observable<DoctorPatientResponse[]> {
    return this._http.get<DoctorPatientResponse[]>(this.jsonUrl, {
      headers: this._authHeaders,
    });
  }

  public fetchDoctorsByPatientId(
    patientId: number
  ): Observable<DoctorPatientResponse[]> {
    const filteredDoctors = this.doctors.filter((doctor) =>
      doctor.patient_ids.some((patient) => patient.patient_id === patientId)
    );
    return of(filteredDoctors);
  }

  get doctorsList(): Observable<Doctor[]> {
    return this.#doctorsList.asObservable();
  }

  public createDoctor(doctorForm: Doctor): Observable<Doctor> {
    const payload: Doctor = {
      firstname: doctorForm.firstname,
      lastname: doctorForm.lastname,
      email: doctorForm.email,
      id_number: doctorForm.id_number,
      phone_number: doctorForm.phone_number,
      profile: doctorForm.profile,
    };
    return this._http.post<any>(
      `${this._apiUrl}/api/client/register`,
      payload,
      { headers: this._authHeaders }
    );
  }

  public editDoctor(id: number, doctorForm: Doctor): Observable<Doctor> {
    const payload: Doctor = {
      firstname: doctorForm.firstname,
      lastname: doctorForm.lastname,
      email: doctorForm.email,
      id_number: doctorForm.id_number,
      phone_number: doctorForm.phone_number,
      profile: doctorForm.profile,
    };

    return this._http.patch<any>(
      `${this._apiUrl}/api/medic/${id}`,
      payload,
      { headers: this._authHeaders }
    );
  }

  public fetchDoctorById(id: number): Observable<DoctorResponse> {
    return this._http.get<DoctorResponse>(`${this._apiUrl}/api/medic/${id}`, {
      headers: this._authHeaders,
    });
  }

  public fetchDoctors(
    queryParams?: Partial<FetchDoctorsQueryParam>
  ): Observable<DoctorsListResponse> {
   
    let params = new HttpParams();

    if (queryParams) {
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value) {
          params = params.set(key, value);
        }
      });
    }

    return this._http
      .get<DoctorsListResponse>(`${this._apiUrl}/api/medic/list`, {
        headers: this._authHeaders,
        params,
      })
      .pipe(
        map((resp) => {
          const doctorsList: DoctorsListResponse = {
            status: resp.status,
            pagination: resp.pagination,
            data: resp.data.map((DoctorResponse) => {
              const doctor: DoctorListItem = {
                id: DoctorResponse?.id,
                medic_id: DoctorResponse?.medic_id ?? null,
                firstname: DoctorResponse?.firstname ?? '',
                lastname: DoctorResponse?.lastname ?? '',
                email: DoctorResponse?.email ?? '',
                type: DoctorResponse?.type ?? '',
                status: DoctorResponse?.status ?? '',
              };
              return doctor;
            }),
          };
          return doctorsList;
        })
      );
  }

  searchDoctors(search: string): void {
    const params = new HttpParams().set('search', search);
    this._http
      .get(`${this._apiUrl}/api/medic/list`, {
        headers: this._authHeaders,
        params,
      })
      .pipe(
        tap((resp: any) => {
          this.#doctorsList.next(resp.data);
        })
      )
      .subscribe();
  }

  public fetchDoctorsSimple(): Observable<any> {
    const url: string = `${this._apiUrl}/api/medic/simple-list?per_page=${this.countMedic}`;
    return this._http.get<any>(url, {
      headers: this._authHeaders,
    });
  }

  public fetchAllDoctorsSimple(): Observable<MedicSimpleListResponse> {
    const url: string = `${this._apiUrl}/api/medic/simple-list?list-format=input`;
    return this._http.get<any>(url, {
      headers: this._authHeaders,
    });
  }

  public unlinkDoctor(id: number): Observable<StatusMessageResponse> {
    const payload = {
      medic_id: id,
    };
    return this._http.post<StatusMessageResponse>(
      `${this._apiUrl}/api/medic/unlink/${id}`,
      payload,
      { headers: this._authHeaders }
    );
  }
}
